'use client';

// ЗАГЛУШКА ФОРМЫ ЭКРАНА (APP-CLAIMS-1020 ред. 9, §2.3, 7.4.2; закон «экран показывается один раз» — CLAUDE.md,
// docs/HOW-IT-WORKS.md §«Данные на фронте и в приложении»).
//
// `<Shape name="bots"/>` — то, что экран рисует, пока свежих данных нет (`usePersistentData` → `loading`): форма этого
// же экрана, снятая при прошлом показе (lib/shape.ts `readShape`: устройство → `shapes/*.json` → сервер → нет), без
// текста и картинок — те же секции, строки тех же высот, места картинок того же размера, вместо текста прямоугольники
// тона `--line` (плашка набора `KSkeleton`). Формы нет — пустое место (никаких «трёх строк по умолчанию» и полосок
// произвольной длины).
// `useShapeRecord(name, ready)` — экран после первого кадра с данными снимает свою форму на устройство.
// Ручные `<Sk w h/>` по страницам уходят (ред. 9, 7.4.3); `Sk` доживает в местах, ещё не переведённых на форму.
import { useEffect, useSyncExternalStore } from 'react';
import { KSkeleton } from '@scriptera/ui/kit';
import { loadServerShapes, readShape, recordShape, shapeKeyOf, type Shape as ShapeData, type ShapeRow } from '@/lib/shape';
import { siteShape } from './shapes';

/** Скругление заглушки — СТУПЕНЬЮ, а не числом (замок вида, П11.2 плана пузырей):
 *  заглушка повторяет то, что под ней встанет, а лесенка у продукта одна.
 *  `round` — кружки (аватарка, точка) и полоски в строку, у которых радиус и так был
 *  в половину толщины. Без ступени заглушка берёт нижнюю (`--r-xs`) из класса `.sk`. */
type SkRadius = 'xs' | 'sm' | 'r' | 'round';

export function Sk({ w, h = 16, r }: { w: number | string; h?: number; r?: SkRadius }) {
  return (
    <span
      className={r ? `sk sk-${r}` : 'sk'}
      style={{ width: typeof w === 'number' ? `${w}px` : w, height: h }}
      aria-hidden="true"
    />
  );
}

/** Высота прямоугольника текста: первая строка (заголовок 17) → 12, остальные (описание 13) → 10 (§2.3). */
const LINE_H = { title: 12, desc: 10 } as const;
/** Длины прямоугольников — доли ширины текстовой колонки, по строкам (без случайных чисел: кадр повторяется). */
const LINE_W = ['46%', '78%', '62%', '70%'];

function ShapeRowView({ row, card = false }: { row: ShapeRow; card?: boolean }) {
  const lines = Math.max(0, Math.min(row.lines ?? 1, 4));
  return (
    <div className={card ? 'shape-row shape-card' : 'shape-row'} data-kind={row.kind} style={{ height: row.h }}>
      {row.img ? (
        <KSkeleton className="shape-img" width={row.img.w} height={row.img.h} radius={row.img.round ? 'round' : 'sm'} />
      ) : null}
      {lines > 0 ? (
        <span className="shape-lines">
          {Array.from({ length: lines }, (_, i) => (
            <KSkeleton key={i} width={LINE_W[i % LINE_W.length]} height={i === 0 ? LINE_H.title : LINE_H.desc} radius="xs" />
          ))}
        </span>
      ) : null}
    </div>
  );
}

/** Нарисовать готовую форму (без поиска). */
export function ShapeView({ shape, groupsClass = 'ai-vgroups', groupClass = 'ai-vgroup' }: {
  shape: ShapeData | null;
  groupsClass?: string;
  groupClass?: string;
}) {
  if (!shape) return <div className="shape shape-none" aria-busy="true" aria-hidden="true" />;
  return (
    <div className={`shape ${groupsClass}`} aria-busy="true" aria-hidden="true" data-shape-of={shape.key}>
      {shape.sections.map((sec) =>
        // Секция `root` — строки без группы (карточки-пузыри прямо в списке): каждая строка — своя карточка.
        sec.id === 'root' ? (
          sec.rows.map((row, i) => <ShapeRowView key={`r${i}`} row={row} card />)
        ) : (
        <div key={sec.id} className={`shape-sec ${groupClass}`}>
          {sec.title ? <span className="shape-title"><KSkeleton width="32%" height={10} radius="xs" /></span> : null}
          {sec.rows.map((row, i) => (
            <ShapeRowView key={i} row={row} />
          ))}
        </div>
        ),
      )}
    </div>
  );
}

const noop = () => () => undefined;

/**
 * Заглушка формы экрана `name` (вид — `view`). Форма читается синхронно, первым же кадром; на сервере — только форма из
 * репо (localStorage там нет), на клиенте — своя с устройства. Поздняя форма показанную заглушку не меняет.
 */
export function Shape({ name, view, groupsClass, groupClass }: {
  name: string;
  view?: Record<string, string>;
  groupsClass?: string;
  groupClass?: string;
}) {
  const id = shapeKeyOf(name, view);
  const shape = useSyncExternalStore(
    noop,
    () => readShapeOnce(id, name, view),
    () => siteShape(id) ?? null,
  );
  // Запасные формы сервера — фоном, один раз за сессию, на будущие заглушки.
  useEffect(() => loadServerShapes(), []);
  return <ShapeView shape={shape} groupsClass={groupsClass} groupClass={groupClass} />;
}

/** Снимок формы для useSyncExternalStore — ОДИН объект на ключ, пока заглушка стоит (иначе бесконечная перерисовка). */
const shapeMemo = new Map<string, ShapeData | null>();
function readShapeOnce(id: string, name: string, view?: Record<string, string>): ShapeData | null {
  if (!shapeMemo.has(id)) shapeMemo.set(id, readShape(name, view));
  return shapeMemo.get(id) ?? null;
}

/**
 * Снять форму экрана после первого кадра с данными (`ready`): корень помечен `data-shape={name}`. Пишется на устройство
 * (`scriptera:shape:<name>|<вид>`) — следующая заглушка этого экрана будет его формы. Раз за маунт.
 */
export function useShapeRecord(name: string, ready: boolean, view?: Record<string, string>): void {
  const id = shapeKeyOf(name, view);
  useEffect(() => {
    if (!ready || typeof requestAnimationFrame === 'undefined') return;
    let cancelled = false;
    // Два кадра: раскладка первого кадра с данными закончена, картинки стоят своими размерами.
    const r1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const got = recordShape(name, view);
        if (got) shapeMemo.set(id, got);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(r1);
    };
    // view — объект на каждый рендер; ключ формы его уже учёл
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, id]);
}
